import type { Parameter } from '@shared/types';

/**
 * Replaces `code` parameter declarations in the SCAD source with real
 * OpenSCAD expression assignments before the code is sent to the compiler.
 *
 * A `code` parameter is declared as a quoted string in the SCAD source:
 *   multiplier = "i * 2"; // [code]
 *
 * Before compilation that line is rewritten to:
 *   multiplier = i * 2;
 *
 * This allows the expression to be used naturally in OpenSCAD (e.g. inside
 * `for` loops where loop variables like `i` are in scope) without having to
 * pass it as a `-D` flag (which only accepts literal values, not expressions).
 *
 * The `[code]` annotation is preserved in the comment so the parser can still
 * identify the parameter type on subsequent parses of the original source.
 */
export function applyCodeSubstitutions(
  code: string,
  parameters: Parameter[],
): string {
  const codeParams = parameters.filter((p) => p.type === 'code');
  if (codeParams.length === 0) return code;

  let result = code;
  for (const param of codeParams) {
    const expression = param.value as string;
    // Replace:  <name> = "<expression>"; // [code] ...optional rest of comment
    // With:     <name> = <expression>;   // [code] ...optional rest of comment
    //
    // The regex preserves leading whitespace, the variable name, and any
    // trailing comment (including the [code] annotation itself so the
    // parameter remains parseable if the source is ever re-read).
    const escapedName = param.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const lineRegex = new RegExp(
      `^([ \\t]*${escapedName}[ \\t]*=[ \\t]*)"[^"]*"([ \\t]*;)([ \\t]*\\/\\/[^\\n]*)?`,
      'gm',
    );
    const escapedExpression = expression.replace(/\$/g, '$$$$');
    result = result.replace(
      lineRegex,
      (_, prefix, semi, comment) =>
        `${prefix}${escapedExpression}${semi}${comment ?? ''}`,
    );
  }
  return result;
}

// ---------------------------------------------------------------------------
// Validation helpers (used by the CodeInput UI component)
// ---------------------------------------------------------------------------

/**
 * OpenSCAD built-in function/constant names that are always valid in
 * expressions and should not be flagged as unknown variables.
 */
const SCAD_BUILTINS = new Set([
  // Constants
  'PI',
  'undef',
  'true',
  'false',
  // Math functions
  'abs', 'ceil', 'floor', 'round', 'sqrt', 'pow', 'exp', 'log', 'ln',
  'sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2',
  'min', 'max', 'norm', 'cross',
  // String / list functions
  'len', 'str', 'chr', 'ord', 'concat', 'lookup', 'search',
  'version', 'version_num', 'parent_module',
  // Type predicates
  'is_undef', 'is_bool', 'is_num', 'is_string', 'is_list', 'is_function',
  // List operations
  'each',
  // Misc
  'assert', 'echo', 'sign', 'rands',
]);

/**
 * Extract identifier tokens from a single-line expression string.
 * Returns an array of unique names, excluding:
 *  - OpenSCAD built-in functions/constants
 *  - Pure numeric literals (handled by the regex boundary)
 */
export function extractIdentifiers(expression: string): string[] {
  const identifierRegex = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\b/g;
  const seen = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = identifierRegex.exec(expression)) !== null) {
    const id = match[1];
    if (!SCAD_BUILTINS.has(id)) {
      seen.add(id);
    }
  }
  return Array.from(seen);
}

/**
 * Returns the names of top-level SCAD variables defined in the source code.
 * Used to validate identifiers in a `code` expression.
 */
export function extractScadVariables(code: string): Set<string> {
  const varRegex = /^[ \t]*(\$?[a-zA-Z_][a-zA-Z0-9_]*)\s*=/gm;
  const names = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = varRegex.exec(code)) !== null) {
    names.add(match[1]);
  }
  return names;
}

/**
 * Returns true if the given parameter name is used anywhere inside a `for`
 * loop body in the source code. When true, the expression may reference loop
 * variables (like `i`, `j`) that are not in the top-level variable list, so
 * identifier validation should be suppressed.
 */
export function isUsedInLoop(paramName: string, code: string): boolean {
  // Heuristic: look for `for (` blocks that contain the param name.
  // We do a simple scan rather than a full AST parse.
  const forRegex = /\bfor\s*\(/g;
  let match: RegExpExecArray | null;
  while ((match = forRegex.exec(code)) !== null) {
    // Find the matching closing parenthesis of the for(...) header
    const start = match.index;
    // Scan from the opening `for (` to find the block body and check for
    // the parameter name within a reasonable window after the for statement.
    const window = code.slice(start, start + 2000);
    // Check if paramName appears in this for-block window
    const paramRegex = new RegExp(`\\b${paramName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
    if (paramRegex.test(window)) return true;
  }
  return false;
}

/**
 * Validates the identifiers in a `code` expression against the known SCAD
 * variable names and the loop context.
 *
 * Returns an array of unknown identifier names (empty = no issues).
 */
export function validateCodeExpression(
  expression: string,
  paramName: string,
  code: string,
  allParameters: Parameter[],
): string[] {
  // If this parameter appears inside a for loop, skip validation entirely
  if (isUsedInLoop(paramName, code)) return [];

  const knownVars = extractScadVariables(code);
  // Also accept the names of all parsed parameters (catches params defined
  // later in the file that the regex might miss due to complex syntax)
  for (const p of allParameters) {
    knownVars.add(p.name);
  }

  const identifiers = extractIdentifiers(expression);
  return identifiers.filter((id) => !knownVars.has(id));
}
